import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
  category: string;
}

export const FrequentQuestions = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentLang, setCurrentLang] = useState(i18n.language);

  // Update language when URL parameter changes
  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang) {
      i18n.changeLanguage(lang).then(() => {
        setCurrentLang(lang);
        setOpenIndex(null);
        setSelectedCategory(null);
      }).catch(error => {
        console.error('Error changing language:', error);
      });
    }
  }, [searchParams, i18n]);

  // Force re-render when language changes
  useEffect(() => {
    setCurrentLang(i18n.language);
  }, [i18n.language]);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Create FAQ items with a key that includes the current language
  const faqItems: FAQItem[] = [
    {
      category: t('faq.categories.usingAiTool', { defaultValue: 'Using the AI Tool' }),
      question: t('faq.questions.howToUseAi.question', { defaultValue: 'How do I use the AI writing assistant?' }),
      answer: t('faq.questions.howToUseAi.answer', { defaultValue: 'Begin typing your review and the AI will automatically suggest improvements and phrases.' })
    },
    {
      category: t('faq.categories.usingAiTool', { defaultValue: 'Using the AI Tool' }),
      question: t('faq.questions.aiSaveData.question', { defaultValue: 'Does the AI tool save what I write?' }),
      answer: t('faq.questions.aiSaveData.answer', { defaultValue: 'No, your input is only processed temporarily.' })
    },
    {
      category: t('faq.categories.writingAndEditing', { defaultValue: 'Writing and Editing Reviews' }),
      question: t('faq.questions.previewReview.question', { defaultValue: 'How do I preview my review?' }),
      answer: (
        <ol className="list-decimal pl-4 space-y-2">
          {t('faq.questions.previewReview.answer', { defaultValue: '1. Click Preview\n2. Review content\n3. Submit when done' }).split('\n').map((step, index) => (
            <li key={index}>{step.replace(/^\d+\.\s*/, '')}</li>
          ))}
        </ol>
      )
    },
    {
      category: t('faq.categories.writingAndEditing', { defaultValue: 'Writing and Editing Reviews' }),
      question: t('faq.questions.editAfterSubmit.question', { defaultValue: 'Can I edit my review after submitting?' }),
      answer: t('faq.questions.editAfterSubmit.answer', { defaultValue: 'Currently, reviews cannot be edited after submission.' })
    },
    {
      category: t('faq.categories.writingAndEditing', { defaultValue: 'Writing and Editing Reviews' }),
      question: t('faq.questions.badExperience.question', { defaultValue: 'What should I do if I have a bad experience?' }),
      answer: t('faq.questions.badExperience.answer', { defaultValue: 'Be specific about what went wrong while remaining professional.' })
    },
    {
      category: t('faq.categories.technicalIssues', { defaultValue: 'Technical Issues' }),
      question: t('faq.questions.aiNotResponding.question', { defaultValue: 'The AI tool is not responding. What should I do?' }),
      answer: (
        <ol className="list-decimal pl-4 space-y-2">
          {t('faq.questions.aiNotResponding.answer', { defaultValue: '1. Check internet\n2. Refresh page\n3. Contact support' }).split('\n').map((step, index) => (
            <li key={index}>{step.replace(/^\d+\.\s*/, '')}</li>
          ))}
        </ol>
      )
    },
    {
      category: t('faq.categories.accountAndPrivacy', { defaultValue: 'Account and Privacy' }),
      question: t('faq.questions.dataProtection.question', { defaultValue: 'How is my data protected?' }),
      answer: t('faq.questions.dataProtection.answer', { defaultValue: 'We take data privacy seriously.' })
    },
    {
      category: t('faq.categories.multilingualSupport', { defaultValue: 'Multilingual Support' }),
      question: t('faq.questions.multilingualSupport.question', { defaultValue: 'Is the platform available in multiple languages?' }),
      answer: t('faq.questions.multilingualSupport.answer', { defaultValue: 'Yes, our platform supports multiple languages.' })
    }
  ];

  const categories = Array.from(new Set(faqItems.map(item => item.category)));
  const filteredItems = selectedCategory 
    ? faqItems.filter(item => item.category === selectedCategory)
    : faqItems;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t('faq.title', { defaultValue: 'Customer Support FAQ' })}
          </h1>
          <p className="text-lg text-gray-600">
            {t('faq.subtitle', { defaultValue: 'Find answers to common questions about our platform' })}
          </p>
        </div>

        <div className="mb-8">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                selectedCategory === null
                  ? 'bg-brand-baseBlue text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t('faq.allCategories', { defaultValue: 'All Categories' })}
            </button>
            {categories.map((category) => (
              <button
                key={`${category}-${currentLang}`}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  selectedCategory === category
                    ? 'bg-brand-baseBlue text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {filteredItems.map((item, index) => (
            <div
              key={`${index}-${currentLang}`}
              className="bg-white rounded-lg shadow-sm overflow-hidden"
            >
              <button
                className="w-full px-6 py-4 text-left focus:outline-none"
                onClick={() => toggleFAQ(index)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-sm text-brand-baseBlue font-medium mb-1 block">
                      {item.category}
                    </span>
                    <h3 className="text-lg font-medium text-gray-900">
                      {item.question}
                    </h3>
                  </div>
                  <span className="ml-6 flex-shrink-0">
                    <svg
                      className={`h-6 w-6 transform ${
                        openIndex === index ? 'rotate-180' : ''
                      } transition-transform duration-200 text-brand-baseBlue`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </div>
              </button>
              {openIndex === index && (
                <div className="px-6 pb-4">
                  <div className="text-gray-600">{item.answer}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center space-y-4">
          <p className="text-gray-600">
            {t('faq.stillNeedHelp', { defaultValue: 'Still need help? Contact our support team at' })}{' '}
            <a href="mailto:admin@echoratings.com" className="text-brand-baseBlue hover:underline">
              admin@echoratings.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}; 